import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Paper, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedIcon from '@mui/icons-material/Verified';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const C = {
  purple: '#552583',
  purpleDark: '#33154f',
  gold: '#FDB927',
  bg: '#E4F0F6',
  white: '#ffffff',
  green: '#22c55e',
};

const VerificationPending = () => {
  const navigate = useNavigate();

  const steps = [
    { label: 'Demande reçue', desc: 'Vos informations ont été enregistrées', done: true },
    { label: 'Vérification en cours', desc: 'Notre équipe examine votre dossier (24-48h)', done: false, active: true },
    { label: 'Notification par email', desc: 'Vous serez informé du résultat par email', done: false },
  ];

  return (
    <Box sx={{ background: C.bg, minHeight: '100vh' }}>

      {/* Hero */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 65%, #7a3aad 100%)`,
        color: C.white, py: { xs: 6, md: 9 }, px: 2, textAlign: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -80, right: -80, width: 240, height: 240, borderRadius: '50%', background: 'rgba(253,185,39,0.07)', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'rgba(253,185,39,0.15)', border: '2px solid rgba(253,185,39,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HourglassTopIcon sx={{ fontSize: 42, color: C.gold }} />
          </Box>
        </Box>
        <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5, fontSize: { xs: '1.7rem', md: '2.3rem' } }}>
          Demande envoyée !
        </Typography>
        <Typography sx={{ opacity: 0.75, maxWidth: 500, mx: 'auto', mb: 3, fontSize: '1rem' }}>
          Merci pour votre inscription. Votre demande est en cours de vérification par notre équipe.
        </Typography>
        <Chip
          icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
          label="Vérification sous 24-48h"
          sx={{
            bgcolor: 'rgba(253,185,39,0.18)', color: C.gold,
            fontWeight: 700, fontSize: '0.78rem',
            border: '1px solid rgba(253,185,39,0.3)',
          }}
        />
      </Box>

      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>

        {/* Etapes */}
        <Paper elevation={0} sx={{
          p: 3.5, mb: 3, borderRadius: '20px', background: C.white,
          border: '1px solid rgba(85,37,131,0.08)',
          boxShadow: '0 4px 20px rgba(85,37,131,0.06)',
        }}>
          <Typography fontWeight={700} sx={{ color: C.purple, mb: 2.5 }}>
            Où en est votre demande
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {steps.map((step, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, position: 'relative' }}>
                {/* Ligne verticale */}
                {i < steps.length - 1 && (
                  <Box sx={{
                    position: 'absolute', left: 15, top: 34, width: 2, height: 'calc(100% - 10px)',
                    background: step.done ? C.green : 'rgba(85,37,131,0.1)',
                  }} />
                )}
                {/* Icone */}
                <Box sx={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: step.done ? 'rgba(34,197,94,0.1)' : step.active ? 'rgba(253,185,39,0.15)' : 'rgba(85,37,131,0.06)',
                  border: step.active ? `2px solid ${C.gold}` : 'none',
                }}>
                  {step.done ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: C.green }} />
                  ) : step.active ? (
                    <HourglassTopIcon sx={{ fontSize: 18, color: C.gold }} />
                  ) : (
                    <EmailIcon sx={{ fontSize: 16, color: 'rgba(85,37,131,0.3)' }} />
                  )}
                </Box>
                {/* Texte */}
                <Box sx={{ pb: 3 }}>
                  <Typography variant="body2" fontWeight={step.done || step.active ? 700 : 500}
                    sx={{ color: step.done ? C.green : step.active ? C.purple : 'rgba(0,0,0,0.4)' }}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.45)' }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Info */}
        <Paper elevation={0} sx={{
          p: 3.5, mb: 3, borderRadius: '20px', background: C.white,
          border: '1px solid rgba(85,37,131,0.08)',
          boxShadow: '0 4px 20px rgba(85,37,131,0.06)',
        }}>
          <Typography fontWeight={700} sx={{ color: C.purple, mb: 2 }}>
            Ce que nous vérifions
          </Typography>
          {[
            'Validité de votre pièce d\'identité',
            'Informations de votre activité',
            'Coordonnées de contact',
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: i < 2 ? 1.5 : 0 }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                background: `linear-gradient(135deg, ${C.purple}, ${C.gold})`,
                flexShrink: 0,
              }} />
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Paper>

        {/* Contact */}
        <Paper elevation={0} sx={{
          p: 3, mb: 3, borderRadius: '20px',
          background: 'rgba(85,37,131,0.04)',
          border: '1px solid rgba(85,37,131,0.08)',
        }}>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)', textAlign: 'center', lineHeight: 1.7 }}>
            Une question ? Contactez-nous à{' '}
            <Typography component="span" variant="body2" fontWeight={700} sx={{ color: C.purple }}>
              contact@usafiri.yt
            </Typography>
          </Typography>
        </Paper>

        {/* Boutons */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{
              py: 1.6, borderRadius: '14px', fontWeight: 700, fontSize: '0.95rem',
              background: `linear-gradient(135deg, ${C.purple}, ${C.gold})`,
              boxShadow: '0 8px 30px rgba(85,37,131,0.3)',
              textTransform: 'none',
              '&:hover': { background: `linear-gradient(135deg, ${C.purpleDark}, #c98a00)` },
            }}
          >
            Retour à l'accueil
          </Button>
          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{
              py: 1.6, borderRadius: '14px', fontWeight: 700, fontSize: '0.95rem',
              textTransform: 'none',
              borderColor: 'rgba(85,37,131,0.3)', color: C.purple,
              '&:hover': { borderColor: C.purple, background: 'rgba(85,37,131,0.04)' },
            }}
          >
            Mon espace
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default VerificationPending;
